import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, DollarSign, Clock, Bell, FileText, TrendingUp,
  Activity, Filter, ChevronRight, Users, Briefcase, AlertCircle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LuxuryBackground, Card, PageHero } from "../components/shared/UIComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  timeline: { icon: Clock, color: "bg-slate-100 text-slate-600", label: "Deal Activity" },
  notification: { icon: Bell, color: "bg-amber-50 text-amber-600", label: "Notification" },
  commitment: { icon: DollarSign, color: "bg-emerald-50 text-emerald-600", label: "Commitment" },
  stage_change: { icon: TrendingUp, color: "bg-blue-50 text-blue-600", label: "Stage Change" },
  document: { icon: FileText, color: "bg-purple-50 text-purple-600", label: "Document" },
};

export default function ActivityFeedPage() {
  const { user } = useAuth();
  const { navigateToEntity } = usePageNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/activity/feed?limit=80`, { headers })
      .then(r => setEvents(r.data.events || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    return events.filter(e => e.type === filter);
  }, [events, filter]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(e => {
      const d = e.timestamp ? new Date(e.timestamp).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : "Unknown Date";
      (groups[d] = groups[d] || []).push(e);
    });
    return Object.entries(groups);
  }, [filtered]);

  const typeCounts = useMemo(() => {
    const counts = { all: events.length };
    events.forEach(e => { counts[e.type] = (counts[e.type] || 0) + 1; });
    return counts;
  }, [events]);

  return (
    <div className="relative" data-testid="activity-feed-page">
      <PageHero eyebrow="Platform Intelligence" title="Activity Feed"
        description="Real-time view of all platform activity relevant to your role." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">

          {/* Filter Chips */}
          <div className="flex gap-2 mb-6 flex-wrap" data-testid="activity-filter-chips">
            {[
              { key: "all", label: "All" },
              ...Object.entries(TYPE_CONFIG).filter(([k]) => typeCounts[k]).map(([k, v]) => ({ key: k, label: v.label })),
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  filter === f.key
                    ? "bg-emerald-700 text-white shadow-sm"
                    : "bg-white border border-cream-warm text-[#666] hover:border-emerald-200"
                }`}>
                {f.label} {typeCounts[f.key] ? `(${typeCounts[f.key]})` : ""}
              </button>
            ))}
          </div>

          {/* Feed */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : grouped.length === 0 ? (
            <Card hover={false}>
              <div className="text-center py-12 text-[#999]">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-lg mb-2">No activity yet</p>
                <p className="text-sm">Activity will appear here as you interact with the platform.</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-6">
              {grouped.map(([date, items], gi) => (
                <motion.div key={date} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: gi * 0.04 }}>
                  <div className="text-xs font-bold text-[#999] uppercase tracking-wider mb-3">{date}</div>
                  <div className="space-y-0">
                    {items.map((event, i) => (
                      <EventRow key={`${event.timestamp}-${i}`} event={event} isLast={i === items.length - 1}
                        onNavigate={() => event.entity_id && navigateToEntity(event.entity_type, event.entity_id)} />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function EventRow({ event, isLast, onNavigate }) {
  const config = TYPE_CONFIG[event.type] || TYPE_CONFIG.timeline;
  const Icon = config.icon;
  const time = event.timestamp ? new Date(event.timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div className="flex gap-3 group" data-testid="activity-event-row">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${config.color} ring-2 ring-white`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        {!isLast && <div className="w-px flex-1 bg-[#e8e0d0] min-h-[12px]" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-4 ${!isLast ? "border-b border-transparent" : ""}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#222] leading-snug">{event.description}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] text-[#bbb]">{time}</span>
              {event.actor && event.actor !== "system" && event.actor !== "platform" && (
                <span className="text-[10px] text-[#999]">by {event.actor}</span>
              )}
              {event.subtype && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                  event.subtype === "approved" || event.subtype === "funded" ? "bg-emerald-100 text-emerald-700" :
                  event.subtype === "pending" ? "bg-amber-100 text-amber-600" :
                  event.subtype === "declined" ? "bg-red-100 text-red-600" :
                  "bg-gray-100 text-gray-600"
                }`}>
                  {event.subtype.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </div>
          {event.entity_id && (
            <button onClick={onNavigate}
              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-cream transition-all" data-testid="event-navigate-btn">
              <ChevronRight className="h-3.5 w-3.5 text-[#999]" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
